// @flow
import React, { Component, type Node } from "react";
import styled, { css } from "styled-components";
import Spinner from "./Spinners/SimpleSpinner";
import { fontSize } from "./common/styled-mixins";

const isIronButton =
  process.env.REACT_APP_DEPLOY_TARGET === "irontrade" ||
  process.env.REACT_APP_DEPLOY_TARGET === "eztrader";

const ButtonView = styled.div`
  direction: ${(props) => props.theme.direction};
  display: inline-block;
  width: 100%;
  padding: 11.5px 14px;
  box-sizing: border-box;
  font-weight: 700;
  font-size: ${fontSize(16)};
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  :hover,
  :focus,
  :active {
    color: #fff;
    outline: none;
  }

  background-color: #706281;
  background: linear-gradient(to bottom, #877c95 0%, #706281 100%);
  :hover {
    background-color: #877c95;
    background: linear-gradient(to bottom, #706281 0%, #877c95 100%);
  }

  ${({ newStyle }) =>
    isIronButton &&
    newStyle &&
    css`
      min-width: 0;
      width: auto;
      padding: 12px 25px;
      font-size: ${fontSize(12)};
      font-weight: bold;
      text-transform: uppercase;
      background: none;
      border: 2px solid #ffffff !important;
      border-radius: 32px;
      box-shadow: none;
      text-shadow: none !important;
      &:hover {
        color: #3b9478;
        background: #fff;
      }
    `};

  ${(props) =>
    props.mobile &&
    css`
      width: auto;
      padding-left: 25px;
      padding-right: 25px;
    `}

    ${(props) =>
      props.big &&
      css`
        min-width: 0px;
        width: auto;
        height: 68px;
        min-height: 68px;
        line-height: 44px;
        font-weight: 400;
        font-size: 27px;

        @media (max-width: 959px) {
          margin-top: 20px;
        }
        @media screen and (max-width: 540px) {
          line-height: 1;
          margin-top: 26px;
          font-size: 19px;
          padding-top: 16px;
          padding-bottom: 16px;
          height: 40px;
          min-height: 40px;
        }
        @media screen and (max-width: 480px) {
          margin-top: 13px;
          font-size: ${fontSize(15)};
          padding-top: 12px;
          padding-bottom: 12px;
        }
        @media (min-width: 1000px) {
          margin-top: 26px;
        }
      `}

  ${(props) =>
    props.wide &&
    css`
      padding-left: 65px;
      padding-right: 65px;
    `}

  ${(props) =>
    props.confirm &&
    css`
      @media (max-width: 959px) {
        padding-top: 11px;
        padding-bottom: 11px;
      }
      @media (max-width: 480px) {
        padding-top: 8px;
        padding-bottom: 8px;
      }
    `}

  ${(props) =>
    props.color === "red" &&
    css`
      background-color: #df5441;
      background: linear-gradient(to bottom, #f0654c 0%, #df5441 100%);
      :hover {
        background-color: #f0654c;
        background: linear-gradient(to bottom, #df5441 0%, #f0654c 100%);
      }
    `};

  ${({ color }) =>
    color === "green" &&
    css`
      background-color: #61ad12;
      background: linear-gradient(to bottom, #68c604 0%, #61ad12 100%);
      :hover {
        background-color: #68c604;
        background: linear-gradient(to bottom, #61ad12 0%, #68c604 100%);
      }

      ${({ newStyle }) =>
        isIronButton &&
        newStyle &&
        css`
          position: relative;
          padding: 9px 40px;
          font-weight: bold;
          color: #ffffff;
          text-decoration: none;
          background: linear-gradient(to left, #47c0c8 0%, #5c6cc0 100%);
          border: none !important;
          border-radius: 32px;
          cursor: pointer;
          z-index: 2;

          ::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            display: block;
            background: linear-gradient(to left, #5c6cc0 0%, #47c0c8 100%);
            border-radius: inherit;
            z-index: -1;
            opacity: 0;
          }

          :hover {
            color: #ffffff;
            background: linear-gradient(to left, #47c0c8 0%, #5c6cc0 100%);
            ::before {
              opacity: 1;
            }
          }
        `};
    `}

  ${({ color, theme, themeLightGreen }) =>
    color === "yellow" &&
    css`
      background-color: ${(props) => props.theme.accent};
      background: linear-gradient(
        to bottom,
        ${(props) => props.theme.tradeHeaderGradientTop} 0%,
        ${(props) => props.theme.tradeHeaderGradientBottom} 100%
      );
      :hover {
        background-color: #fd9500;
        background: linear-gradient(
          to bottom,
          ${(props) => props.theme.tradeHeaderGradientBottom} 0%,
          ${(props) => props.theme.tradeHeaderGradientTop} 100%
        );
      }

      ${({ newStyle }) =>
        isIronButton &&
        newStyle &&
        css`
          padding: 12px 25px;
          font-size: ${fontSize(12)};
          font-weight: bold;
          text-transform: uppercase;
          background: none;
          border: 2px solid #ffffff !important;
          border-radius: 32px;
          box-shadow: none;
          text-shadow: none !important;
          :hover {
            color: #3b9478;
            background: #fff;
          }

          ${themeLightGreen &&
            css`
              color: #3b9478;
              border: 2px solid #3b9478 !important;
              &:hover {
                color: #ffffff;
                background: #3b9478;
              }
            `};
        `};
    `};

  ${(props) =>
    props.smallFont &&
    css`
      @media (max-width: 479px) {
        font-size: ${fontSize(14)};
      }
    `};

  ${(props) =>
    props.withShadow &&
    css`
      font-weight: 500;
      text-shadow: -1px -1px rgba(0, 0, 0, 0.2);
      border: none;
      border-bottom: 2px solid;
      border-bottom-color: rgba(0, 0, 0, 0.3);
    `};

  ${(props) =>
    props.disabled &&
    css`
      background-color: #a4a7b4 !important;
      background: linear-gradient(
        to bottom,
        #bdc0cf 0%,
        #a4a7b4 100%
      ) !important;
      pointer-events: none !important;
      user-select: none !important;
      cursor: default !important;
    `};

  ${(props) =>
    props.fixshit &&
    css`
      width: auto;
    `}

  ${({ color }) =>
    color === "mint" &&
    css`
      font-size: 16px;
      font-weight: bold;
      color: #fff;
      background: #3b9478;
      border-radius: 8px;

      :hover {
        color: #fff;
        background: #2e735d;
      }
    `};

  ${({ color, disabled }) =>
    color === "mint" &&
    disabled &&
    css`
      background: #bebebe !important;
    `};

  ${({ invited }) =>
    invited &&
    css`
      background: #0085eb !important;
      min-height: 40px !important;
      line-height: 20px !important;

      :before {
        display: none;
      }

      :after {
        display: none;
      }
    `};

  ${({ minWidth }) =>
    minWidth &&
    css`
      min-width: ${minWidth};
    `}
`;

const Text = styled.span`
  display: inline-block;
  vertical-align: middle;

  ${({ theme }) =>
    theme.locale !== "ar" &&
    css`
      :first-letter {
        text-transform: uppercase;
      }
    `};
`;

type Props = {
  title?: string,
  wide?: boolean,
  big?: boolean,
  mobile?: boolean,
  color?: string,
  desktop?: boolean,
  disabled?: boolean,
  confirm?: boolean,
  onClick?: (e: SyntheticEvent<HTMLElement>) => void,
  smallFont?: boolean,
  showSpinner?: boolean,
  children?: Node,
  withShadow?: boolean,
  newStyle?: boolean,
  className?: string
};

export default class Button extends Component<Props, {}> {
  onClick = (e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault();

    if (!this.props.disabled && this.props.onClick !== undefined) {
      this.props.onClick(e);
    }
  };

  render() {
    const {
      title,
      wide,
      big,
      mobile,
      color = "violet",
      confirm,
      showSpinner,
      smallFont,
      withShadow,
      newStyle,
      minWidth,
      className,
      ...other
    } = this.props;

    return (
      <ButtonView
        title={title}
        wide={wide}
        big={big}
        mobile={mobile}
        confirm={confirm}
        color={color}
        onClick={this.onClick}
        smallFont={smallFont}
        withShadow={withShadow}
        newStyle={newStyle}
        minWidth={minWidth}
        className={className}
        {...other}
      >
        {showSpinner === true && <Spinner />}
        <Text>{this.props.children || title}</Text>
      </ButtonView>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Button.js